import { Routes } from '@angular/router';
import { HomeComponent } from './features/home/home.component';
import { CollectionsComponent } from './features/collections/collections.component';
import { StoryComponent } from './features/story/story.component';
import { ProductDetailComponent } from './features/product/product-detail.component';
import { EditorialComponent, EditorialContent } from './features/editorial/editorial.component';
import { FaqsComponent } from './features/faqs/faqs.component';
import { ContactComponent } from './features/contact/contact.component';
import { CheckoutComponent } from './features/checkout/checkout.component';
import { CheckoutPayComponent } from './features/checkout/checkout-pay.component';
import { CheckoutSuccessComponent } from './features/checkout/checkout-success.component';
import { CheckoutCancelComponent } from './features/checkout/checkout-cancel.component';
import { PublicLayoutComponent } from './layout/public-layout/public-layout.component';
import { authGuard, portalAdminGuard, userManagerGuard, chatAdminGuard } from './core/guards/auth.guard';
import { LoginComponent } from '@ecommerce/admin/features/login/login.component';
import { ForgotPasswordComponent } from '@ecommerce/admin/features/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from '@ecommerce/admin/features/reset-password/reset-password.component';
import { AdminLayoutComponent } from './layout/layouts/admin-layout/admin-layout.component';
import { AdminDashboardComponent } from '@ecommerce/admin/features/admin-dashboard/admin-dashboard.component';
import { UsersAdminComponent } from '@ecommerce/admin/features/users-admin/users-admin.component';
import { SettingsAdminComponent } from '@ecommerce/admin/features/settings-admin/settings-admin.component';
import { ChatAdminComponent } from '@ecommerce/admin/features/chat-admin/chat-admin.component';
import { OrdersAdminComponent } from '@ecommerce/admin/features/orders-admin/orders-admin.component';
import { BrandsAdminComponent } from '@ecommerce/admin/features/brands-admin/brands-admin.component';
import { CategoriesAdminComponent } from '@ecommerce/admin/features/categories-admin/categories-admin.component';
import { CategoryFiltersAdminComponent } from '@ecommerce/admin/features/category-filters-admin/category-filters-admin.component';
import { CategorySpecsAdminComponent } from '@ecommerce/admin/features/category-specs-admin/category-specs-admin.component';
import { ProductsAdminComponent } from '@ecommerce/admin/features/products-admin/products-admin.component';
import { AiMarketingHubComponent } from '@ecommerce/admin/features/ai-marketing-admin/ai-marketing-hub.component';
import { AiMarketingCreativesComponent } from '@ecommerce/admin/features/ai-marketing-admin/ai-marketing-creatives.component';
import { AiMarketingOrganicComponent } from '@ecommerce/admin/features/ai-marketing-admin/ai-marketing-organic.component';
import { AiMarketingAssistantComponent } from '@ecommerce/admin/features/ai-marketing-admin/ai-marketing-assistant.component';
import { BundlesAdminComponent } from '@ecommerce/admin/features/bundles-admin/bundles-admin.component';

const shipping: EditorialContent = {
  eyebrow: 'Shipping Policy',
  title: 'Shipping',
  paragraphs: [
    'Orders are packed and shipped from the United States. Delivery times vary by destination.',
    'Shipping charges, if any, are calculated at checkout.'
  ]
};

const returns: EditorialContent = {
  eyebrow: 'Refund and Returns Policy',
  title: 'Returns',
  paragraphs: [
    'Unused items in original packaging may be returned according to the policy published at checkout.',
    'Opened fragrance oils and used diffusers cannot be restocked. Contact us if a unit arrives damaged.'
  ]
};

const privacy: EditorialContent = {
  eyebrow: 'Privacy Policy',
  title: 'Privacy',
  paragraphs: [
    'We collect only what is needed to fulfill an order and to respond to your messages.',
    'We do not sell personal information.'
  ]
};

const terms: EditorialContent = {
  eyebrow: 'Terms of Service',
  title: 'Terms of service',
  paragraphs: [
    'Orders are confirmed when payment is received. Descriptions, prices, and availability may change.',
    'This storefront is for purchasing Diffuser Dreams products for personal or business use.'
  ]
};

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent
  },
  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        component: AdminDashboardComponent
      },
      {
        path: 'users',
        canActivate: [userManagerGuard],
        component: UsersAdminComponent
      },
      {
        path: 'settings',
        canActivate: [portalAdminGuard],
        component: SettingsAdminComponent
      },
      {
        path: 'chat',
        canActivate: [chatAdminGuard],
        component: ChatAdminComponent
      },
      {
        path: 'chat/:conversationId',
        canActivate: [chatAdminGuard],
        component: ChatAdminComponent
      },
      {
        path: 'orders',
        component: OrdersAdminComponent
      },
      {
        path: 'brands',
        component: BrandsAdminComponent
      },
      {
        path: 'categories',
        component: CategoriesAdminComponent
      },
      {
        path: 'category-filters',
        component: CategoryFiltersAdminComponent
      },
      {
        path: 'category-specs',
        component: CategorySpecsAdminComponent
      },
      {
        path: 'products',
        component: ProductsAdminComponent
      },
      {
        path: 'ai-marketing',
        component: AiMarketingHubComponent
      },
      {
        path: 'ai-marketing/connect',
        redirectTo: 'ai-marketing',
        pathMatch: 'full'
      },
      {
        path: 'ai-marketing/assistant',
        component: AiMarketingAssistantComponent
      },
      {
        path: 'ai-marketing/creatives',
        component: AiMarketingCreativesComponent
      },
      {
        path: 'ai-marketing/organic',
        component: AiMarketingOrganicComponent
      },
      {
        path: 'bundles',
        component: BundlesAdminComponent
      }
    ]
  },
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      { path: '', component: HomeComponent },
      { path: 'shop', component: CollectionsComponent },
      { path: 'shop/:categorySlug', component: CollectionsComponent },
      { path: 'collections', redirectTo: 'shop', pathMatch: 'full' },
      { path: 'product/:slug', component: ProductDetailComponent },
      { path: 'story', component: StoryComponent },
      { path: 'faqs', component: FaqsComponent },
      { path: 'shipping', component: EditorialComponent, data: { page: shipping } },
      { path: 'returns', component: EditorialComponent, data: { page: returns } },
      { path: 'contact', component: ContactComponent },
      { path: 'privacy', component: EditorialComponent, data: { page: privacy } },
      { path: 'terms', component: EditorialComponent, data: { page: terms } },
      { path: 'checkout', component: CheckoutComponent },
      { path: 'checkout/pay', component: CheckoutPayComponent },
      { path: 'checkout/success', component: CheckoutSuccessComponent },
      { path: 'checkout/cancel', component: CheckoutCancelComponent }
    ]
  },
  { path: '**', redirectTo: '' }
];
